// Hiscores of the current map, shown while Tab is held.

import { online, type Board } from '../online/client';
import { fillBoard } from './boardList';

const $ = (id: string) => document.getElementById(id)!;

export interface BoardInfo {
  /** Server board key, or null when this map has no online hiscores. */
  key: string | null;
  title: string;
  rules: string;
  rulesLabel: string;
  /** Shown under the list (why there are no hiscores, or that times aren't checked). */
  note: string;
}

export class Scoreboard {
  private readonly el = $('scores');
  private info: BoardInfo = { key: null, title: '', rules: '', rulesLabel: '', note: '' };
  private data: Board | null = null;
  private error = '';
  private fetchedAt = 0;
  private seq = 0;
  private visible = false;

  constructor() {
    online.onChange(() => this.refresh());
  }

  get currentKey(): string | null {
    return this.info.key;
  }

  /** The player's best on this board, in ticks (known after a fetch). */
  get myBest(): number | null {
    return this.data?.me?.ticks ?? null;
  }

  setBoard(info: BoardInfo) {
    const same = info.key === this.info.key && info.rules === this.info.rules;
    this.info = info;
    if (!same) {
      this.data = null;
      this.error = '';
      this.fetchedAt = 0;
      this.refresh();
    } else this.render();
  }

  refresh() {
    const { key, rules } = this.info;
    const seq = ++this.seq;
    if (!key) {
      this.render();
      return;
    }
    this.fetchedAt = performance.now();
    online
      .board(key, rules, 10)
      .then((b) => {
        if (seq !== this.seq) return;
        this.data = b;
        this.error = '';
        this.render();
      })
      .catch((e: Error) => {
        if (seq !== this.seq) return;
        this.error = e.message;
        this.render();
      });
  }

  setVisible(on: boolean) {
    if (on === this.visible) return;
    this.visible = on;
    this.el.hidden = !on;
    if (on) {
      if (this.info.key && performance.now() - this.fetchedAt > 20_000) this.refresh();
      this.render();
    }
  }

  private render() {
    if (!this.visible) return;
    const { info, data } = this;
    $('scoresMap').textContent = info.title;
    $('scoresRules').textContent = info.rulesLabel;
    const list = $('scoresList');
    list.textContent = '';
    let status = '';
    if (!info.key) status = info.note;
    else if (this.error) status = this.error;
    else if (!data) status = 'Loading hiscores…';
    else if (!data.top.length) status = 'No times yet. Finish the map to be first.';
    if (data && info.key) fillBoard(list, data, online.me?.name.toLowerCase(), online.me?.name);
    const foot = [
      status,
      info.key && data ? `${data.total} player${data.total === 1 ? '' : 's'}` : '',
      info.key && !online.me ? 'Pick a name in Settings to post your times.' : '',
      info.key && info.note ? info.note : '',
    ].filter(Boolean);
    $('scoresNote').textContent = foot.join(' · ');
  }
}
